import { CircularProgress } from "@/components/display/CircularProgress";
import { curatedSalesPersonImage } from "@/lib/sales-display-notifications";
import { formatMNOK, formatNOK } from "@/lib/format";
import type { SalesUnitDisplayMetric } from "@/lib/sales-display-metrics";
import type { SalesPerson } from "@/hooks/use-sales-dashboard-data";

interface DisplaySalesUnitSlideProps {
  metric: SalesUnitDisplayMetric;
  people: SalesPerson[];
  monthLabel: string;
  year: number;
}

function Person({ person }: { person: SalesPerson }) {
  const imageUrl = curatedSalesPersonImage(person.name) ?? person.avatarUrl;
  const initials = person.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2);
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-2xl font-bold">
        {imageUrl ? <img src={imageUrl} alt="" className="h-full w-full object-cover" /> : initials}
      </div>
      <span className="max-w-40 truncate text-lg font-semibold">{person.name}</span>
    </div>
  );
}

/** TV slide: one equal-weight slide per sales unit, with project-level attributed values. */
export function DisplaySalesUnitSlide({ metric, people, monthLabel, year }: DisplaySalesUnitSlideProps) {
  const members = metric.memberUserIds
    .map((userId) => people.find((person) => person.userId === userId))
    .filter((person): person is SalesPerson => Boolean(person));
  const remainingKr = Math.max(0, metric.annualTargetKr - metric.achievedGeekKr);

  return (
    <div className="grid h-full grid-cols-[1.15fr_0.85fr] items-center gap-16 px-8">
      <section className="flex flex-col items-center gap-8 text-center">
        <div>
          <p className="text-xl font-medium uppercase tracking-[0.22em] text-muted-foreground">Selger</p>
          <h2 className="mt-2 text-6xl font-bold">{metric.unitName}</h2>
        </div>
        {members.length > 0 && <div className="flex justify-center gap-6">{members.map((person) => <Person key={person.userId} person={person} />)}</div>}
        <div>
          <p className="text-2xl text-muted-foreground">Signert salg · {monthLabel}</p>
          <p className="mt-2 text-8xl font-bold tabular-nums">{formatMNOK(metric.monthSalesKr)}</p>
        </div>
        <div className="flex gap-12 text-2xl">
          <p><span className="font-bold tabular-nums">{metric.monthDealCount}</span> nye salg</p>
          <p><span className="font-bold tabular-nums">{formatNOK(metric.monthEstimatedGeekKr)}</span> estimert GEEK</p>
        </div>
      </section>

      <section className="flex flex-col items-center gap-7 rounded-[2.5rem] border border-border/70 bg-card/60 px-12 py-12 shadow-sm">
        <div className="text-center">
          <p className="text-xl uppercase tracking-[0.2em] text-muted-foreground">Hittil i {year}</p>
          <h3 className="mt-2 text-3xl font-bold">Reell GEEK mot felles årsmål</h3>
        </div>
        <CircularProgress value={metric.achievementPct ?? 0} size={230} strokeWidth={19} label="av årsmål" />
        <div className="text-center">
          <p className="text-5xl font-bold tabular-nums">{formatMNOK(metric.achievedGeekKr)}</p>
          <p className="mt-2 text-xl text-muted-foreground">av {formatMNOK(metric.annualTargetKr)}</p>
        </div>
        <p className="text-xl font-semibold">
          {remainingKr === 0 && metric.annualTargetKr > 0 ? "Målet er nådd! 🎉" : `${formatNOK(remainingKr)} gjenstår`}
        </p>
      </section>
    </div>
  );
}
